/**
 * Formatting Hooks
 * Provides settings-aware formatting functions that use SettingsContext
 */

import { useMemo } from 'react';
import { useSettings } from '../contexts/SettingsContext';

/**
 * Hook to get formatting functions that respect user preferences
 * @returns {Object} - Object with formatting functions
 */
export const useFormatting = () => {
  const settings = useSettings();

  return useMemo(() => {
    /**
     * Format date based on user preference
     * @param {Date|string} date - Date to format
     * @returns {string} - Formatted date string
     */
    const formatDate = (date) => {
      if (!date) return '';
      
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      
      const format = settings.dateFormat;
      
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      switch (format) {
        case 'DD/MM/YYYY':
          return `${day}/${month}/${year}`;
        case 'YYYY-MM-DD':
          return `${year}-${month}-${day}`;
        case 'MMM DD, YYYY':
          return `${monthNames[dateObj.getMonth()]} ${day}, ${year}`;
        case 'DD MMM YYYY':
          return `${day} ${monthNames[dateObj.getMonth()]} ${year}`;
        case 'MM/DD/YYYY':
        default:
          return `${month}/${day}/${year}`;
      }
    };

    /**
     * Format currency based on user preference
     * @param {number} amount - Amount to format
     * @param {string} [currency] - Currency code (optional, uses preference if not provided)
     * @returns {string} - Formatted currency string
     */
    const formatCurrency = (amount, currency = null) => {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      
      const currencyCode = currency || settings.currencyFormat;
      
      const currencySymbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        JPY: '¥'
      };
      
      const symbol = currencySymbols[currencyCode] || '$';
      
      // For JPY, don't show decimals
      if (currencyCode === 'JPY') {
        return `${symbol}${Math.round(amount).toLocaleString()}`;
      }
      
      return `${symbol}${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    };

    /**
     * Format number based on user preference
     * @param {number} num - Number to format
     * @returns {string} - Formatted number string
     */
    const formatNumber = (num) => {
      if (num === null || num === undefined || isNaN(num)) return '0';
      
      const format = settings.numberFormat;
      
      switch (format) {
        case 'compact':
          if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
          if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
          return num.toString();
        case 'scientific':
          return num.toExponential(2);
        case 'standard':
        default:
          return num.toLocaleString();
      }
    };

    return {
      formatDate,
      formatCurrency,
      formatNumber,
    };
  }, [settings.dateFormat, settings.currencyFormat, settings.numberFormat]);
};

